// @flow

import { createStore, applyMiddleware, compose } from 'redux';
import type { Store, Reducer, Middleware, StoreEnhancer } from 'redux';

import loggerMiddleware from 'common/middleware/logger';

/**
 * Configures a redux store. For development, adds action logging, dev tools and a hook to bolt on
 * hot reload functionality.
 */
export default function configureStore<
  S: Object,
  A: { type: any, ... }
>(options: {|
  rootReducer: Reducer<S, A>,
  middlewares?: Array<Middleware<S, A>>,
  hookToStore?: (store: Store<S, A>) => void
  // There are variety of different Dispatch types, so we can't provide the third
  // type argument for Store here with ease.
|}): Store<S, A, any> {
  const middlewares = options.middlewares || [];
  if (__DEV__) {
    middlewares.push(loggerMiddleware);
  }

  const storeEnhancer: StoreEnhancer<*, *, *> = compose(
    applyMiddleware(...middlewares),
    // https://github.com/zalmoxisus/redux-devtools-extension
    typeof window === 'object' &&
      typeof window.devToolsExtension !== 'undefined'
      ? window.devToolsExtension()
      : f => f
  );
  const store = createStore(options.rootReducer, storeEnhancer);

  if (options.hookToStore) {
    options.hookToStore(store);
  }

  return store;
}
